import { useEffect, useCallback, useState } from 'react';
import { Form, Select, Input, Flex, Divider, Modal } from 'antd';
import type { FindOrganizationDto } from '@evimo/types/api/organization/find-organization/find-organization.dto';
import type { CreateOrganizationRequestDto } from '@evimo/types/api/organization/create-organization/create-organization.request.dto';
import { OrganizationType } from '@evimo/core/modules/organization/domain/OrganizationType';
import { Language } from '@evimo/types/enums/language';
import { useTranslation } from 'react-i18next';
import { ALLOWED_ORGANIZATION_TYPES } from '~/app/constants/organizationAllowedTypes.types';
import { CreateOrganizationResponseDto } from '@evimo/types/api/organization/create-organization/create-organization.response.dto';
import { useAppToast } from '~/app/hooks/useAppToast';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '~/app/components/common/Button';
import { OrganizationCsmsSection } from './OrganizationCsmsSection/OrganizationCsmsSection';
import {
  buildExternalCsmsConfigPayload,
  formStateFromConfig,
  isExternalCsmsProxyEnabled,
  csmsSaveConfirmation,
  validateCsmsBaseUrl,
  type ExternalCsmsFormState,
} from './organizationExternalCsms.utils';
import { readOrganizationClientError } from '~/app/services/organization/readOrganizationClientError';

const { Option } = Select;

type Props = {
  organization: FindOrganizationDto | null;
  existingNames: string[];
  parentOptions?: { value: string; label: string }[];
  onSubmit: (
    dto: CreateOrganizationRequestDto,
  ) => Promise<CreateOrganizationResponseDto>;
  onCancel: () => void;
};
type FormValues = {
  name: string;
  organizationType: OrganizationType;
  active: boolean;
  mainLanguage: Language;
  parentId: string | null;
};

export function OrganizationDialogForm({
  organization,
  existingNames,
  parentOptions,
  onSubmit,
  onCancel,
}: Props) {
  const [form] = Form.useForm<FormValues>();
  const { t } = useTranslation('organization');
  const { showSuccess, showError } = useAppToast();
  const queryClient = useQueryClient();
  const isEditMode = organization !== null;
  const organizationType = Form.useWatch('organizationType', form);
  const [csms, setCsms] = useState<ExternalCsmsFormState>(() =>
    formStateFromConfig(organization?.externalCsmsConfig),
  );
  const [csmsError, setCsmsError] = useState<string | null>(null);
  const isPartnerType =
    organizationType === OrganizationType.Landlord ||
    organizationType === OrganizationType.OEM;

  useEffect(() => {
    if (organization) {
      form.setFieldsValue({
        name: organization.name,
        organizationType: organization.organizationType as OrganizationType,
        active: organization.active,
        mainLanguage: organization.mainLanguage as Language,
        parentId: organization.parentId ?? null,
      });
      setCsms(formStateFromConfig(organization.externalCsmsConfig));
    } else {
      form.resetFields();
      setCsms(formStateFromConfig(null));
    }
    setCsmsError(null);
  }, [organization, form]);

  const nameValidator = useCallback(
    (_: unknown, val: string) => {
      if (!val) return Promise.resolve();
      const isDuplicate =
        existingNames.includes(val) &&
        (!organization || organization.name !== val);
      return isDuplicate
        ? Promise.reject(t('dialog.nameExists'))
        : Promise.resolve();
    },
    [existingNames, organization, t],
  );

  const submitOrganization = async (values: FormValues) => {
    const validationError = validateCsmsForm(csms, t);
    if (validationError) {
      setCsmsError(validationError);
      return;
    }
    setCsmsError(null);

    const externalCsmsConfig = buildExternalCsmsConfigPayload(
      csms,
      organization?.externalCsmsConfig,
    );

    try {
      await onSubmit({
        name: values.name,
        organizationType: values.organizationType,
        mainLanguage: values.mainLanguage,
        active: values.active,
        parentId: isPartnerType ? null : (values.parentId ?? null),
        externalCsmsConfig,
      });
      await queryClient.invalidateQueries({
        queryKey: ['organization', organization?.id],
      });
      await queryClient.invalidateQueries({ queryKey: ['organization'] });
      await queryClient.invalidateQueries({ queryKey: ['assets', 'detail'] });
      await queryClient.invalidateQueries({
        queryKey: ['assets', 'ocpp-settings'],
      });
      showSuccess('notifications.saved', 'Organization updated successfully');
    } catch (error) {
      const apiError = await readOrganizationClientError(error);
      if (apiError) {
        setCsmsError(apiError);
        return;
      }
      showError(
        'errors.unexpectedRequest',
        'An error occurred while saving organization',
      );
    }
  };

  const handleFinish = async (values: FormValues) => {
    const confirmation = csmsSaveConfirmation(
      csms,
      organization?.externalCsmsConfig,
    );
    const copy = {
      update: {
        title: t('dialog.csmsSaveTitle'),
        content: t('dialog.csmsSaveBody'),
      },
      clear: {
        title: t('dialog.csmsClearTitle'),
        content: t('dialog.csmsClearBody'),
      },
      primary: {
        title: t('dialog.csmsPrimaryChangeTitle'),
        content: t('dialog.csmsPrimaryChangeBody'),
      },
      removeSecondary: {
        title: t('dialog.csmsRemoveSecondaryTitle'),
        content: t('dialog.csmsRemoveSecondaryBody'),
      },
      disableSecondary: {
        title: t('dialog.csmsDisableSecondaryTitle'),
        content: t('dialog.csmsDisableSecondaryBody'),
      },
    }[confirmation ?? 'update'];

    Modal.confirm({
      title: copy.title,
      content: copy.content,
      okText: t('dialog.confirm'),
      cancelText: t('dialog.cancel'),
      onOk: () => submitOrganization(values),
    });
  };

  const handleRemoveSecondary = (index: number) => {
    setCsms({
      ...csms,
      secondaries: (csms.secondaries ?? []).filter(
        (_, endpointIndex) => endpointIndex !== index,
      ),
    });
  };

  return (
    <Form<FormValues>
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      initialValues={{
        organizationType: OrganizationType.Operator,
        active: true,
        mainLanguage: Language.EN,
        parentId: null,
      }}
    >
      <Form.Item
        label={t('dialog.nameLabel')}
        name="name"
        rules={[
          { required: true, message: t('dialog.nameRequired') },
          { validator: nameValidator },
        ]}
      >
        <Input
          autoFocus
          placeholder={t('dialog.namePlaceholder', 'Enter organization name')}
        />
      </Form.Item>

      <Form.Item
        label={t('dialog.typeLabel')}
        name="organizationType"
        rules={[{ required: true, message: t('dialog.typeRequired') }]}
      >
        <Select
          placeholder={t('dialog.typePlaceholder', 'Select organization type')}
        >
          {ALLOWED_ORGANIZATION_TYPES.map((type) => (
            <Option key={type} value={type}>
              {t(`dialog.types.${type}`)}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label={t('dialog.statusLabel')}
        name="active"
        rules={[{ required: true, message: t('dialog.statusRequired') }]}
      >
        <Select placeholder={t('dialog.statusPlaceholder', 'Select status')}>
          <Option value={true}>{t('table.active')}</Option>
          <Option value={false}>{t('table.inactive')}</Option>
        </Select>
      </Form.Item>

      <Form.Item
        label={t('dialog.languageLabel')}
        name="mainLanguage"
        rules={[{ required: true, message: t('dialog.languageRequired') }]}
      >
        <Select
          placeholder={t('dialog.languagePlaceholder', 'Select main language')}
        >
          {Object.values(Language).map((lang) => (
            <Option key={lang} value={lang}>
              {lang.toUpperCase()}
            </Option>
          ))}
        </Select>
      </Form.Item>

      {!isPartnerType && (
        <Form.Item
          label={t('dialog.parentLabel', 'Managed By')}
          name="parentId"
        >
          {parentOptions ? (
            <Select
              allowClear
              showSearch={{ optionFilterProp: 'label' }}
              placeholder={t(
                'dialog.parentPlaceholder',
                'Select parent organization',
              )}
            >
              {parentOptions.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          ) : (
            <Input
              placeholder={t('dialog.parentIdPlaceholder', 'Enter parent ID')}
            />
          )}
        </Form.Item>
      )}

      <Divider titlePlacement="left" className="app-my-4">
        {t('dialog.ocppSectionTitle')}
      </Divider>

      <OrganizationCsmsSection
        value={csms}
        primaryAlreadySaved={isExternalCsmsProxyEnabled(
          organization?.externalCsmsConfig,
        )}
        savedSecondaryCount={
          organization?.externalCsmsConfig?.endpoints?.filter(
            (endpoint) => endpoint.role === 'secondary',
          ).length ?? 0
        }
        apiError={csmsError}
        onChange={setCsms}
        onRemoveSecondary={handleRemoveSecondary}
      />

      <Flex justify="flex-end" gap="small" className="app-mt-8">
        <Button onClick={onCancel}>{t('dialog.cancel')}</Button>
        <Button type="primary" htmlType="submit">
          {isEditMode ? t('dialog.save') : t('dialog.create')}
        </Button>
      </Flex>
    </Form>
  );
}

function validateCsmsForm(
  csms: ExternalCsmsFormState,
  t: (key: string) => string,
): string | null {
  if (!csms.enableCsms) {
    if ((csms.secondaries?.length ?? 0) > 0) {
      return t('dialog.csmsPrimaryLocked');
    }
    return null;
  }
  if (!validateCsmsBaseUrl(csms.csmsBaseUrl)) {
    return t('dialog.csmsUrlInvalid');
  }
  const invalidSecondary = (csms.secondaries ?? []).some(
    (endpoint) => !validateCsmsBaseUrl(endpoint.baseUrl),
  );
  if (invalidSecondary) {
    return t('dialog.csmsUrlInvalid');
  }
  return null;
}
